import { useState, useEffect } from "react";
import textInputModel from "../../../models/general/elements/inputElements/textInput.model";
import TextInputElementComponent from "../../general/elements/inputElements/TextInputElementComponent";
import TextAreaInputElementComponent from "../../general/elements/inputElements/TextAreaInputElementComponent";
import { category as categoryModel } from "../../../models/categories/category.model";
import "../../../styles/components/dialogs/categories/addOrUpdateCategoryDialogComponent.scss";
import categoryActions from "../../../store/actions/items/categories/category.actions";
import { connect } from "react-redux";
import { bindActionCreators } from "@reduxjs/toolkit";
import validationHelper from "../../../helpers/validation.helper";
import AlertService from "../../../services/alerts/alert.service";
import { useNavigate, useSearchParams } from "react-router-dom";

export const AddOrUpdateCategoryDialogComponent = ({ categoryActions }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [categoryDetails, setCategoryDetails] = useState(categoryModel);
  const [imageFile, setImageFile] = useState(null);
  const [errors, setErrors] = useState({});
  const actionType = searchParams.get("action");
  const categoryId = searchParams.get("id");

  useEffect(() => {
    if (actionType === "update" && categoryId) {
      fetchCategoryDetails();
    }
  }, [actionType, categoryId]);

  const fetchCategoryDetails = async () => {
    try {
      const response = await categoryActions.getCategoryById(categoryId);
      if (!validationHelper.validateHttpResponse(response, true)) {
        AlertService.error("שגיאה בטעינת פרטי הקטגוריה", "אנא נסה שוב מאוחר יותר");
        return;
      }
      setCategoryDetails(response.payload);
    } catch (error) {
      AlertService.error("שגיאה בטעינת פרטי הקטגוריה", "אנא נסה שוב מאוחר יותר");
      console.error("Error fetching category details:", error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCategoryDetails({ ...categoryDetails, [name]: value });
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    setImageFile(file);
  };

  const validateForm = () => {
    const newErrors = {};

    if (!categoryDetails.categoryName || categoryDetails.categoryName.trim() === "") {
      newErrors.categoryName = "שם הקטגוריה הוא שדה חובה";
    } else if (!validationHelper.validateText(categoryDetails.categoryName, true)) {
      newErrors.categoryName = "שם הקטגוריה חייב להכיל אותיות בלבד";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!validateForm()) return;

    try {
      let response;
      if (actionType === "update") {
        response = await categoryActions.updateCategory(categoryDetails);
      } else {
        response = await categoryActions.addCategory(categoryDetails);
      }

      if (!validationHelper.validateHttpResponse(response, actionType === "update" ? false : true)) {
        setErrors({
          general: actionType === "update" ? "עדכון הקטגוריה נכשל" : "הוספת קטגוריה נכשלה. נסה שוב.",
        });
        return;
      }

      if (actionType === "update") {
        AlertService.success("הקטגוריה עודכנה בהצלחה");
      } else {
        searchParams.set("action", "connect");
        searchParams.set("id", response.payload);
        setSearchParams(searchParams);
        AlertService.success("קטגוריה נוספה בהצלחה", "מיד תנווט לעמוד שיוך קבוצות פריטים");
      }

      categoryActions.getCategories({ getAll: true });

      if (actionType !== "update") {
        setCategoryDetails(categoryModel);
        setErrors({});
      }
    } catch (error) {
      setErrors({
        general: actionType === "update" ? "שגיאה בעדכון הקטגוריה. נסה שוב מאוחר יותר." : "שגיאה בשרת. נסה שוב מאוחר יותר.",
      });
      console.error(actionType === "update" ? "Update category error:" : "Add category error:", error);
    }
  };

  const handleUpdateImage = async () => {
    if (!imageFile || !categoryId) {
      AlertService.error("אנא בחר תמונה ולאחר מכן נסה שוב.");
      return;
    }

    const formData = new FormData();
    formData.append("file", imageFile);

    try {
      const response = await categoryActions.updateCategoryImage(categoryId, formData);
      if (!validationHelper.validateHttpResponse(response)) {
        AlertService.error("שגיאה בעדכון התמונה", "אנא נסה שוב מאוחר יותר");
        return;
      }

      AlertService.success("התמונה עודכנה בהצלחה");
      fetchCategoryDetails();
    } catch (error) {
      AlertService.error("שגיאה בעדכון התמונה", "אנא נסה שוב מאוחר יותר");
      console.error("Update category image error:", error);
    }
  };

  const getCategoryNameInputElement = () => {
    const inputProps = {
      ...textInputModel,
      name: "categoryName",
      error: errors.categoryName,
      value: categoryDetails.categoryName,
      onChange: handleInputChange,
      label: "שם הקטגוריה",
    };
    return <TextInputElementComponent props={inputProps} />;
  };

  const getCategoryDisplayOrderInputElement = () => {
    const inputProps = {
      ...textInputModel,
      name: "displayOrder",
      error: errors.displayOrder,
      value: categoryDetails.displayOrder,
      onChange: handleInputChange,
      label: "סדר תצוגה",
      type: "number",
    };
    return <TextInputElementComponent props={inputProps} />;
  };

  const getCategoryDescriptionInputElement = () => {
    const inputProps = {
      ...textInputModel,
      name: "description",
      error: errors.description,
      value: categoryDetails.description,
      onChange: handleInputChange,
      label: "תיאור הקטגוריה",
    };
    return <TextAreaInputElementComponent props={inputProps} />;
  };

  const getCategoryImageInputElement = () => {
    if (actionType === "add") return;

    return (
      <div className="dialogContainer imageUploadContainer">
        <label>עדכן תמונה:</label>
        <input type="file" accept="image/*" onChange={handleImageChange} />
        <button type="button" className="secondaryButton" onClick={handleUpdateImage}>
          עדכון תמונה
        </button>
      </div>
    );
  };

  return (
    <div className="addCategoryDialogContainer">
      <h1>{actionType === "update" ? "עדכון קטגוריה" : "הוסף קטגוריה"}</h1>
      <form onSubmit={handleSubmit}>
        {getCategoryNameInputElement()}
        {getCategoryDisplayOrderInputElement()}
        {getCategoryDescriptionInputElement()}
        {getCategoryImageInputElement()}

        <button type="submit" className="primaryButton">
          {actionType === "update" ? "עדכון" : "הוסף"}
        </button>
        {errors.general && <span className="error">{errors.general}</span>}
      </form>
    </div>
  );
};

const mapStateToProps = (state, ownProps) => ({});

const mapDispatchToProps = (dispatch) => {
  return {
    categoryActions: bindActionCreators(categoryActions, dispatch),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(AddOrUpdateCategoryDialogComponent);
